import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { ArrowLeft, User, Phone, Mail, MessageCircle, FileText, Package, Receipt, Wallet, Image as ImageIcon, MessageSquareWarning, Repeat } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import { toast } from "sonner";

const TABS = [
  { key: "overview", label: "Overview", icon: User },
  { key: "quotes", label: "Quotes", icon: FileText },
  { key: "orders", label: "Orders", icon: Package },
  { key: "invoices", label: "Invoices", icon: Receipt },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "files", label: "Files", icon: ImageIcon },
  { key: "complaints", label: "Complaints", icon: MessageSquareWarning },
];

export default function CustomerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => { api.get(`/customers/${id}/history`).then(r => setD(r.data)); }, [id]);
  if (!d) return <div className="p-8 text-[#757169]">Loading customer…</div>;
  const c = d.customer;

  const repeatOrder = async () => {
    try {
      const { data } = await api.post(`/customers/${id}/repeat-order`);
      toast.success(`Draft order ${data.order_no} created from last order`);
      nav(`/orders/${data.id}`);
    } catch(e) { toast.error(e.response?.data?.detail || "No previous order to clone"); }
  };

  return (
    <div className="space-y-6">
      <Link to="/customers" className="btn-ghost text-xs inline-flex items-center gap-1"><ArrowLeft size={12}/> Customers</Link>

      <div className="card-surface p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-tiny mb-1">Customer</div>
            <h1 className="font-display text-3xl tracking-tight">{c.name}</h1>
            <div className="text-sm text-[#757169] mt-2 space-y-1">
              {c.company && <div>{c.company}</div>}
              {c.phone && <div className="flex items-center gap-1.5"><Phone size={12}/> <span className="mono">{c.phone}</span></div>}
              {c.email && <div className="flex items-center gap-1.5"><Mail size={12}/> {c.email}</div>}
              {c.address && <div className="text-xs">{c.address}</div>}
            </div>
          </div>
          {c.phone && <WhatsAppButton phone={c.phone} message={`Hi ${c.name}, just checking in from Marsh Clothing.`} label="WhatsApp" size="lg" testid="cust-wa"/>}
          {d.orders.length > 0 && (
            <button onClick={repeatOrder} className="btn-primary flex items-center gap-2 mt-2 md:mt-0" data-testid="repeat-order-btn">
              <Repeat size={14}/> Repeat last order
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Orders" value={d.summary.total_orders} />
        <Kpi label="Quotes" value={d.summary.total_quotes} />
        <Kpi label="Revenue" value={inr(d.summary.total_revenue)} />
        <Kpi label="Paid" value={inr(d.summary.total_paid)} />
        <Kpi label="Outstanding" value={inr(d.summary.outstanding)} tone={d.summary.outstanding>0?"text-[#B83A3A]":""} />
        <Kpi label="Real profit" value={inr(d.summary.profit)} tone={d.summary.profit>=0?"text-[#4A7258]":"text-[#B83A3A]"} />
      </div>

      <div className="border-b border-[#EAE5DB] flex gap-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              tab===t.key ? "border-[#C45A41] text-[#C45A41]" : "border-transparent text-[#757169] hover:text-[#2C2B29]"
            }`} style={{fontFamily:"Manrope, sans-serif"}}
            data-testid={`cust-tab-${t.key}`}>
            <t.icon size={13}/> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Panel title="Recent leads" empty="No leads recorded.">
            {d.leads.slice(0,5).map(l => (
              <div key={l.id} className="flex justify-between text-sm py-1.5 border-b border-[#EAE5DB] last:border-0">
                <span>{l.customer_name} · <span className="text-[#757169]">{l.source||"—"}</span></span>
                <span className="badge badge-neutral">{l.status}</span>
              </div>
            ))}
          </Panel>
          <Panel title="Recent orders" empty="No orders yet.">
            {d.orders.slice(0,5).map(o => (
              <Link key={o.id} to={`/orders/${o.id}`} className="flex justify-between text-sm py-1.5 border-b border-[#EAE5DB] last:border-0 hover:bg-[#F4EDDF] px-2 -mx-2 rounded">
                <span className="mono">{o.order_no}</span>
                <span className="mono">{inr(o.total)}</span>
              </Link>
            ))}
          </Panel>
          {c.notes && (
            <div className="card-surface p-5 md:col-span-2">
              <div className="label-tiny mb-2">Customer notes</div>
              <div className="text-sm whitespace-pre-line">{c.notes}</div>
            </div>
          )}
        </div>
      )}

      {tab === "quotes" && <TableList rows={d.quotes} cols={[
        { key: "quote_no", label: "Quote", cls: "mono", render: r => <a href={`/q/${r.token}`} target="_blank" rel="noreferrer" className="hover:underline">{r.quote_no}</a> },
        { key: "status", label: "Status", render: r => <span className={`badge ${r.status==="accepted"?"badge-success":r.status==="declined"?"badge-danger":"badge-neutral"}`}>{r.status}</span> },
        { key: "total", label: "Total", cls: "mono", render: r => inr(r.total) },
        { key: "created_at", label: "Created", cls: "mono text-xs", render: r => r.created_at?.slice(0,10) },
      ]} />}

      {tab === "orders" && <TableList rows={d.orders} cols={[
        { key: "order_no", label: "Order", cls: "mono", render: r => <Link to={`/orders/${r.id}`} className="hover:underline">{r.order_no}</Link> },
        { key: "stage", label: "Stage", render: r => <span className="badge badge-warn">{r.stage}</span> },
        { key: "total", label: "Total", cls: "mono", render: r => inr(r.total) },
        { key: "created_at", label: "Date", cls: "mono text-xs", render: r => r.created_at?.slice(0,10) },
      ]} />}

      {tab === "invoices" && <TableList rows={d.invoices} cols={[
        { key: "invoice_no", label: "Invoice", cls: "mono font-semibold", render: r => <a href={`/print/invoice/${r.id}`} target="_blank" rel="noreferrer" className="hover:underline">{r.invoice_no}</a> },
        { key: "status", label: "Status", render: r => <span className={`badge ${r.status==="paid"?"badge-success":r.status==="partial"?"badge-warn":"badge-neutral"}`}>{r.status}</span> },
        { key: "total", label: "Total", cls: "mono", render: r => inr(r.total) },
        { key: "amount_paid", label: "Paid", cls: "mono", render: r => inr(r.amount_paid) },
      ]} />}

      {tab === "payments" && <TableList rows={d.payments} cols={[
        { key: "amount", label: "Amount", cls: "mono", render: r => inr(r.amount) },
        { key: "method", label: "Method" },
        { key: "reference", label: "Reference" },
        { key: "received_at", label: "When", cls: "mono text-xs", render: r => r.received_at?.slice(0,10) },
      ]} />}

      {tab === "files" && (d.attachments.length === 0 ? <Empty label="No files uploaded." /> :
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {d.attachments.map(a => (
            <a key={a.id} href={a.file_url} target="_blank" rel="noreferrer" className="card-surface overflow-hidden hover:shadow-md transition-shadow">
              {a.file_url?.startsWith("data:image") || /\.(png|jpe?g|gif|webp|svg)$/i.test(a.file_name)
                ? <img src={a.file_url} alt={a.file_name} className="w-full h-28 object-cover" />
                : <div className="h-28 bg-[#F6F1E7] flex items-center justify-center text-[#757169] text-xs p-2 text-center">{a.file_name}</div>}
              <div className="p-3">
                <div className="text-xs font-medium truncate">{a.file_name}</div>
                <div className="text-[10px] text-[#757169] uppercase tracking-wider mt-0.5">{a.type}</div>
              </div>
            </a>
          ))}
        </div>
      )}

      {tab === "complaints" && <TableList rows={d.complaints} cols={[
        { key: "subject", label: "Subject" },
        { key: "description", label: "Description" },
        { key: "status", label: "Status", render: r => <span className={`badge ${r.status==="resolved"?"badge-success":"badge-warn"}`}>{r.status}</span> },
      ]} />}
    </div>
  );
}

const Kpi = ({label, value, tone}) => (
  <div className="card-surface p-4">
    <div className="label-tiny">{label}</div>
    <div className={`font-display text-lg font-bold mt-1 ${tone||""}`}>{value}</div>
  </div>
);
const Panel = ({title, children, empty}) => (
  <div className="card-surface p-5">
    <div className="label-tiny mb-3">{title}</div>
    {React.Children.count(children) === 0 ? <div className="text-sm text-[#757169]">{empty}</div> : children}
  </div>
);
const Empty = ({label}) => <div className="card-surface p-8 text-center text-[#757169] text-sm">{label}</div>;
const TableList = ({ rows, cols }) => (
  rows.length === 0 ? <Empty label="Nothing to show." /> : (
    <div className="card-surface overflow-x-auto">
      <table className="data-table">
        <thead><tr>{cols.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r,i) => (
            <tr key={r.id || i}>
              {cols.map(c => <td key={c.key} className={c.cls}>{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);
